// 캔버스 분기 슬롯 탭 오버레이 — **branch마다 슬롯 탭 바 1개**를 띄운다(중첩 branch 포함, 활성
// 경로에 렌더된 것 전부). editing 캔버스는 활성 슬롯의 children만 렌더하므로(조건 미평가) 이 탭이
// 유일한 슬롯 전환 수단이다(속성 패널과 공유 state = activeBranchSlots).
//
// 배치는 **자기 branch 노드 rect 좌상단 바로 위**이고 **전체 슬롯을 상시** 보여준다(2026-08-31
// 사용자 확정 — 원래 방식). 같은 날 두 대안을 시도했다가 둘 다 되돌렸다: A4 박스 왼쪽 여백 열은
// 시선이 문서와 열 사이를 왕복해야 했고, 활성 슬롯만 담은 컴팩트 칩 + hover 확장은 선택지를 보려면
// 마우스를 올려야 해서 "어떤 분기가 있는지"가 화면에서 사라졌다. 표시·상호작용은 작성 탭 오버레이와
// 한 벌을 공유한다(BranchSlotTabBar).
//   - 아랫변을 노드 윗변에 맞추므로(nodeTabBarBox) 한 줄 바는 제자리, 줄이 접히면 위로만 자란다.
//   - 겹치는 바만 아래로 밀어낸다(branchTabLayout) — 노드 위 배치에서는 x가 노드마다 다르므로
//     좌우로 비켜 선 바들은 서로를 밀지 않는다.
//
// 한때(2026-07-03) 중첩 탭 겹침을 없애려고 "최상위 branch rect 한 곳에 활성 경로 체인
// (`[슬롯A|슬롯B] → [슬롯C|슬롯D] → …`) 1개"로 몰아 놓았는데, root branch가 문서 전체를 감싸는
// 템플릿(TBM)에서는 **모든 분기 탭이 문서 맨 위로 몰려** 어느 탭이 어느 노드 것인지 알 수 없었다.
// 겹침은 자리를 합치는 대신 밀어내기로 푼다 — 탭과 노드의 대응이 남는다.
//
// 좌표는 BindingBadgeOverlay와 동일하게 [data-composer-node-id] 요소 rect를 캔버스 상대좌표로 환산한다.
// 편집 캔버스 전용 오버레이라 인쇄/미리보기/상세에는 포함되지 않는다.

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ComposerNode } from '@safetyhub/report-composer';
import { getNodeRect, rectRelativeTo } from './canvasRect';
import { renderedBranchTabBars, type BranchTabBar } from './chainOfBranches';
import {
  estimatedBarTop,
  nodeTabBarBox,
  resolveTabBarOverlaps,
  type TabBarBox,
} from './branchTabLayout';
import { BranchSlotTabBar, tabBarSelector } from './BranchSlotTabBar';

/**
 * 렌더된 탭 바들을 실측해 최종 top(기준 + 겹침 밀기)을 계산하고, **그 자리에서 DOM에 반영**한다.
 *
 * ⚠️ 기준 top/left는 **노드 rect에서 매번 다시 계산**한다 — 바 자신의 현재 위치를 기준으로 삼으면
 * 이미 적용된 밀기가 다음 회차의 기준이 돼 바가 커밋마다 계속 내려간다(발산). 바에서 가져오는 건
 * 폭·높이뿐이고, 그 둘은 위치를 옮겨도 변하지 않으므로(가로 상한이 상수) 계산이 수렴한다.
 * height가 기준 top에 들어가지만(아랫변 고정) 그 반대 경로가 없어 되먹임이 아니다.
 *
 * ⚠️ 계산 결과를 setState에만 맡기면 **F9 연결선이 한 커밋 늦게 옛 자리에 남는다**: setState는
 * 이 오버레이만 다시 렌더하고, 형제인 BranchLinkOverlay의 재측정은 다음 커밋에야 돈다(그쪽은 탭
 * rect를 실측해 선 출발점을 잡는다 — 2026-08-31 memo 제거로 부모 재렌더는 따라오지만 "같은 커밋"
 * 보장은 여전히 여기 몫). style.top을 직접 맞춰 두면 **같은 커밋 안에서** 뒤따라 도는 형제
 * layout effect가 이미 밀린 위치를 재고, paint 전에 정리된다.
 *
 * 반환값은 오프셋이 아니라 **최종 top 자체**다. 아랫변 고정이라 기준 top이 실측 높이에 달려 있어,
 * 오프셋만 넘기면 다음 렌더의 JSX가 그 기준을 혼자 재현하지 못해 DOM과 어긋난다.
 */
function measureAndApplyTops(
  canvas: HTMLElement,
  bars: BranchTabBar[],
  nodeRects: Record<string, NodeAnchor>,
): Record<string, number> {
  const boxes: TabBarBox[] = [];
  const elements = new Map<string, HTMLElement>();
  for (const bar of bars) {
    const nodeRect = nodeRects[bar.branchId];
    if (!nodeRect) continue;
    const el = canvas.querySelector<HTMLElement>(tabBarSelector('body', bar.branchId));
    if (!el) continue; // 첫 커밋/렌더 직전 — 다음 측정 tick에서 잡힌다.
    const barRect = rectRelativeTo(canvas, el);
    elements.set(bar.branchId, el);
    boxes.push(
      nodeTabBarBox(bar.branchId, nodeRect.top, nodeRect.left, barRect.width, barRect.height),
    );
  }

  const offsets = resolveTabBarOverlaps(boxes);
  const tops: Record<string, number> = {};
  for (const box of boxes) {
    const top = box.top + (offsets[box.id] ?? 0);
    tops[box.id] = top;
    // 안 밀린 바에도 다시 써 둔다(React가 렌더한 값과 같아 무해 — 밀렸다가 겹침이 풀린 바가 옛
    // 자리를 붙든 채 남지 않게).
    const el = elements.get(box.id);
    if (el) el.style.top = `${top}px`;
  }
  return tops;
}

/** 바의 기준이 되는 branch 노드 좌상단(캔버스 상대좌표) — 실측값이라 state로 들고 다닌다. */
interface NodeAnchor {
  top: number;
  left: number;
}

/**
 * 각 바의 기준 노드 rect를 잰다. **layout effect에서만** 부른다 — 렌더 본문에서 재면 커밋 전
 * 레이아웃을 강제로 읽게 되고, 같은 값을 측정 단계가 한 번 더 재느라 기준이 두 곳으로 갈라진다.
 */
function measureNodeAnchors(
  canvas: HTMLElement,
  bars: BranchTabBar[],
): Record<string, NodeAnchor> {
  const anchors: Record<string, NodeAnchor> = {};
  for (const bar of bars) {
    const rect = getNodeRect(canvas, bar.branchId);
    // 노드가 아직/이미 DOM에 없으면 기준이 없다 — 그 바는 이번 회차에 그리지 않는다.
    if (rect) anchors[bar.branchId] = { top: rect.top, left: rect.left };
  }
  return anchors;
}

/**
 * 분기 슬롯 탭 오버레이 — 캔버스 div 내부(RenderTemplate 뒤, position:absolute)에 둔다.
 * 바는 자기 branch 노드 바로 위에 뜨고 pointer-events는 탭에만 활성(노드 클릭 방해 X).
 * 탭 클릭 시 그 branch의 활성 슬롯을 전환하고 노드를 선택한다.
 */
export function BranchSlotTabsOverlay({
  canvas,
  body,
  activeBranchSlots,
  missedBranchIds,
  onSelectSlot,
  onSelectBranch,
}: {
  canvas: HTMLElement | null;
  body: ComposerNode[];
  activeBranchSlots: Record<string, number>;
  /**
   * 분기 슬롯 시뮬레이션에서 필터 일치 데이터를 못 찾아 현장 값으로 fallback된 branchId 집합(선택).
   * 해당 branch의 바에 경고 배지(⚠)를 붙인다. 미전달/빈 집합이면 배지 없음.
   */
  missedBranchIds?: ReadonlySet<string>;
  /** 슬롯 탭 클릭 → 그 branch의 활성 슬롯을 index로 전환. */
  onSelectSlot: (branchId: string, index: number) => void;
  /** 탭 클릭 시 branch 노드도 선택(속성 패널 노출). */
  onSelectBranch: (branchId: string) => void;
}) {
  // 탭 목록 파생은 순수(JSON) — DOM은 아래 겹침 측정 단계에서만 본다.
  const bars = useMemo(
    () => renderedBranchTabBars(body, activeBranchSlots),
    [body, activeBranchSlots],
  );

  // 기준 노드 rect와 최종 top 둘 다 **DOM 실측 결과**라 렌더 본문이 아니라 layout effect에서만
  // 만든다(BranchLinkOverlay/BindingBadgeOverlay와 같은 관례).
  const [nodeRects, setNodeRects] = useState<Record<string, NodeAnchor>>({});
  const [tops, setTops] = useState<Record<string, number>>({});
  const sigRef = useRef<string>('');

  // ⚠️ 측정은 커밋 후(useLayoutEffect), deps 없이 매 커밋마다 수행하고 시그니처가 바뀔 때만
  // setState(BranchLinkOverlay/BindingBadgeOverlay와 동일 관례). layout effect라 오프셋 보정이
  // paint 전에 반영돼 밀리기 전 위치가 화면에 비치지 않는다. 슬롯 라벨·개수가 바뀌어 줄 접힘이
  // 달라지는 경우도 이 경로로 재측정된다(높이가 바뀌면 기준 top과 겹침 관계가 함께 바뀐다).
  //
  // 기준 rect와 top을 **한 tick에 같이** 잰다. 바가 아직 안 그려진 첫 회차엔 tops가 비지만,
  // setNodeRects로 바가 그려진 뒤 곧바로 이 effect가 다시 돌아 실측 top이 채워진다 — layout
  // effect의 setState는 paint 전에 동기 flush되므로 추정 위치가 화면에 비치지 않는다.
  useLayoutEffect(() => {
    const nextRects = canvas ? measureNodeAnchors(canvas, bars) : {};
    const nextTops = canvas ? measureAndApplyTops(canvas, bars, nextRects) : {};
    const sig = JSON.stringify([nextRects, nextTops]);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setNodeRects(nextRects);
      setTops(nextTops);
    }
  }); // deps 없음 — 매 커밋(재측정 tick 포함) 후 최신 DOM 측정

  if (!canvas) return null;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden={false}>
      {bars.map((bar) => {
        const rect = nodeRects[bar.branchId];
        if (!rect) return null;
        return (
          <BranchSlotTabBar
            key={`branch-tabs-${bar.branchId}`}
            surface="body"
            measureKey={bar.branchId}
            branchId={bar.branchId}
            slots={bar.slots}
            activeIndex={bar.activeIndex}
            // 실측된 최종 top(기준 + 겹침 밀기)을 그대로 쓴다. 아직 측정 전인 첫 커밋만 한 줄 바
            // 기준의 추정값으로 시작하고, 같은 커밋의 layout effect가 실측값으로 덮는다(paint 전).
            top={tops[bar.branchId] ?? estimatedBarTop(rect.top)}
            left={rect.left}
            missed={missedBranchIds?.has(bar.branchId)}
            onSelectSlot={(index) => {
              onSelectSlot(bar.branchId, index);
              onSelectBranch(bar.branchId);
            }}
          />
        );
      })}
    </div>
  );
}
